import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Feature } from '../lib/types';
import { cn } from '../lib/utils';
import pricingConfig from '../data/pricingConfig.json';

interface HourCalculationProps {
  selectedFeatures: Feature[];
  componentCount: number;
  isMigration?: boolean;
  postTypeCount?: number;
  isContentUpload?: boolean;
  contentUploadLevel?: 'light' | 'medium' | 'heavy';
}

const HourCalculation: React.FC<HourCalculationProps> = ({ 
  selectedFeatures, 
  componentCount,
  isMigration = false,
  postTypeCount = 0,
  isContentUpload = false,
  contentUploadLevel = 'light'
}) => {
  const [activeTab, setActiveTab] = useState<'external' | 'internal'>('external');
  const featuresMinHours = selectedFeatures.reduce((sum, feature) => sum + feature.minHours, 0);
  const featuresMaxHours = selectedFeatures.reduce((sum, feature) => sum + feature.maxHours, 0);
  
  const componentsHours = componentCount * 3;
  const migrationHours = isMigration ? postTypeCount * 2 : 0;
  
  // Calculate content upload hours based on level
  const getContentUploadHours = () => {
    if (!isContentUpload) return 0;
    switch (contentUploadLevel) {
      case 'light': return 4;
      case 'medium': return 8;
      case 'heavy': return 12;
      default: return 0;
    }
  };
  
  const contentUploadHours = getContentUploadHours();
  
  const totalMinHours = featuresMinHours + componentsHours + migrationHours + contentUploadHours;
  const totalMaxHours = featuresMaxHours + componentsHours + migrationHours + contentUploadHours;
  
  // Determine component tier based on count
  const getComponentTier = (count: number): number => {
    if (count < 5) return 1;
    if (count <= 10) return 2;
    if (count <= 15) return 3;
    return 4;
  };
  
  const componentTier = getComponentTier(componentCount);
  
  // Format hours display based on whether min and max are the same
  const formatHours = (min: number, max: number): string => {
    return min === max ? `${min} hrs` : `${min}-${max} hrs`;
  };
  
  // Get content upload level label
  const getContentUploadLevelLabel = () => {
    switch (contentUploadLevel) {
      case 'light': return 'Light';
      case 'medium': return 'Medium';
      case 'heavy': return 'Heavy';
      default: return 'Unknown';
    }
  };
  
  // Separate standard features from custom features
  const standardFeatures = selectedFeatures.filter(feature => 
    !feature.id.startsWith('custom-feature-') && feature.subsection !== 'Custom Features'
  );
  
  const customFeatures = selectedFeatures.filter(feature => 
    feature.id.startsWith('custom-feature-') || feature.subsection === 'Custom Features'
  );
  
  // Handle copying pricing breakdown to Word document
  const handleCopyToWord = () => {
    let content = `PRICING BREAKDOWN (${activeTab.toUpperCase()})\n\n`;
    
    if (activeTab === 'external') {
      // External pricing breakdown
      
      // Add components
      if (componentCount > 0) {
        content += `Components (${componentCount}) - ${componentsHours} hours\n`;
        content += `${pricingConfig.components.external.description}\n\n`;
      }
      
      // Add content upload
      if (isContentUpload) {
        content += `Content Upload (${getContentUploadLevelLabel()}) - ${contentUploadHours} hours\n`;
        content += `${pricingConfig.contentUpload[contentUploadLevel].external.description}\n\n`;
      }
      
      // Add migration
      if (isMigration && postTypeCount > 0) {
        content += `Content Migration (${postTypeCount} types) - ${migrationHours} hours\n`;
        content += `${pricingConfig.migration.external.description}\n\n`;
      }
      
      // Add selected features
      selectedFeatures.forEach(feature => {
        const hours = formatHours(feature.minHours, feature.maxHours).replace(' hrs', ' hours');
        const featureConfig = pricingConfig.features as any;
        const featureInfo = featureConfig[feature.id];
        const description = featureInfo?.external?.description || feature.description;
        content += `${feature.name} - ${hours}\n`;
        content += `${description}\n\n`;
      });
      
    } else {
      // Internal pricing breakdown
      
      // Add components
      if (componentCount > 0) {
        content += `Components (${componentCount}) - ${componentsHours} hours\n`;
        content += `${pricingConfig.components.internal.description}\n`;
        content += `${pricingConfig.components.internal.additionalNotes}\n\n`;
      }
      
      // Add content upload
      if (isContentUpload) {
        content += `Content Upload (${getContentUploadLevelLabel()}) - ${contentUploadHours} hours\n`;
        content += `${pricingConfig.contentUpload[contentUploadLevel].internal.description}\n`;
        content += `${pricingConfig.contentUpload[contentUploadLevel].internal.additionalNotes}\n\n`;
      }
      
      // Add migration
      if (isMigration && postTypeCount > 0) {
        content += `Content Migration (${postTypeCount} types) - ${migrationHours} hours\n`;
        content += `${pricingConfig.migration.internal.description}\n`;
        content += `${pricingConfig.migration.internal.additionalNotes}\n\n`;
      }
      
      // Add selected features
      selectedFeatures.forEach(feature => {
        const hours = formatHours(feature.minHours, feature.maxHours).replace(' hrs', ' hours');
        const featureConfig = pricingConfig.features as any;
        const featureInfo = featureConfig[feature.id];
        const description = featureInfo?.internal?.description || feature.description;
        const additionalNotes = featureInfo?.internal?.additionalNotes;
        content += `${feature.name} - ${hours}\n`;
        content += `${description}\n`;
        if (additionalNotes) {
          content += `${additionalNotes}\n`;
        }
        content += '\n';
      });
      
      // Add internal notes
      if (pricingConfig.generalNotes.internal.length > 0) {
        content += "INTERNAL NOTES:\n";
        pricingConfig.generalNotes.internal.forEach(note => {
          content += `• ${note}\n`;
        });
        content += '\n';
      }
    }
    
    // Add total
    content += `Total Estimate - ${formatHours(totalMinHours, totalMaxHours).replace(' hrs', ' hours')}\n`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(content).then(
      () => {
        // You could add a toast notification here if you have one available
        alert(`${activeTab === 'external' ? 'External' : 'Internal'} pricing breakdown copied to clipboard! You can now paste it into a Word document.`);
      },
      (err) => {
        console.error('Could not copy text: ', err);
        alert('Failed to copy to clipboard. Please try again.');
      }
    );
  };
  
  return (
    <div className="space-y-4">
      {selectedFeatures.length === 0 && componentCount === 1 && !isMigration && !isContentUpload ? (
        <p className="text-muted-foreground">Select features to see hour estimates</p>
      ) : (
        <>
          {componentCount > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>Components ({componentCount}) - Tier {componentTier}</span>
              <span className="font-medium">{componentsHours} hrs</span>
            </div>
          )}
          
          {isContentUpload && (
            <div className="flex justify-between py-2 border-b">
              <span>Content Upload ({getContentUploadLevelLabel()})</span>
              <span className="font-medium">{contentUploadHours} hrs</span>
            </div>
          )}
          
          {isMigration && postTypeCount > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>Content Migration ({postTypeCount} types)</span>
              <span className="font-medium">{migrationHours} hrs</span>
            </div>
          )}
          
          {/* Standard Features */}
          {standardFeatures.length > 0 && (
            <div className="space-y-2">
              {standardFeatures.map(feature => (
                <div key={feature.id} className="flex justify-between py-2 border-b">
                  <span>{feature.name}</span>
                  <span className="font-medium">
                    {formatHours(feature.minHours, feature.maxHours)}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {/* Custom Features */}
          {customFeatures.length > 0 && (
            <div className="space-y-2">
              {customFeatures.length > 0 && (
                <h4 className="text-sm font-medium pt-2">Custom Features</h4>
              )}
              {customFeatures.map(feature => (
                <div key={feature.id} className="flex justify-between py-2 border-b">
                  <span>{feature.name}</span>
                  <span className="font-medium">
                    {formatHours(feature.minHours, feature.maxHours)}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          <div className="pt-3 mt-3 border-t border-primary/20">
            <div className="flex justify-between font-bold">
              <span>Total Estimate</span>
              <span>{formatHours(totalMinHours, totalMaxHours)}</span>
            </div>
          </div>
          
          <Card className="bg-secondary mt-6">
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-medium">Detailed Pricing Breakdown</h4>
                <button
                  onClick={handleCopyToWord}
                  className="p-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                  title="Copy to Word Document"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"></path>
                  </svg>
                </button>
              </div>
              
              {/* Tabs */}
              <div className="flex border-b mb-4">
                <button
                  onClick={() => setActiveTab('external')}
                  className={cn(
                    "px-3 py-2 text-xs font-medium transition-colors",
                    activeTab === 'external' 
                      ? "border-b-2 border-primary text-primary" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  External
                </button>
                <button
                  onClick={() => setActiveTab('internal')}
                  className={cn(
                    "px-3 py-2 text-xs font-medium transition-colors",
                    activeTab === 'internal' 
                      ? "border-b-2 border-primary text-primary" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  Internal
                </button>
              </div>
              
              {/* External Tab Content */}
              {activeTab === 'external' && (
                <div className="text-xs space-y-3">
                  {/* Components */}
                  {componentCount > 0 && (
                    <p className="text-muted-foreground">
                      <span className="font-medium">Components ({componentCount}) - {componentsHours} hours</span>
                      <br />
                      {pricingConfig.components.external.description}
                    </p>
                  )}
                  
                  {/* Content Upload */}
                  {isContentUpload && (
                    <p className="text-muted-foreground">
                      <span className="font-medium">Content Upload ({getContentUploadLevelLabel()}) - {contentUploadHours} hours</span>
                      <br />
                      {pricingConfig.contentUpload[contentUploadLevel].external.description}
                    </p>
                  )}
                  
                  {/* Migration */}
                  {isMigration && postTypeCount > 0 && (
                    <p className="text-muted-foreground">
                      <span className="font-medium">Content Migration ({postTypeCount} types) - {migrationHours} hours</span>
                      <br />
                      {pricingConfig.migration.external.description}
                    </p>
                  )}
                  
                  {/* Selected Features - This will include Global Setup if it's checked */}
                  {selectedFeatures.map(feature => {
                    // Safe feature lookup to avoid any potential type issues
                    const featureConfig = pricingConfig.features as any;
                    const featureInfo = featureConfig[feature.id];
                    const description = featureInfo?.external?.description || feature.description;
                    
                    return (
                      <p key={feature.id} className="text-muted-foreground">
                        <span className="font-medium">{feature.name} - {formatHours(feature.minHours, feature.maxHours).replace(' hrs', ' hours')}</span>
                        <br />
                        {description}
                      </p>
                    );
                  })}
                  
                  {/* Total */}
                  <p className="text-muted-foreground font-bold pt-2 border-t border-border/50">
                    <span className="font-bold">Total Estimate - {formatHours(totalMinHours, totalMaxHours).replace(' hrs', ' hours')}</span>
                  </p>
                </div>
              )}
              
              {/* Internal Tab Content */}
              {activeTab === 'internal' && (
                <div className="text-xs space-y-3">
                  {/* Components */}
                  {componentCount > 0 && (
                    <p className="text-muted-foreground">
                      <span className="font-medium">Components ({componentCount}) - {componentsHours} hours</span>
                      <br />
                      {pricingConfig.components.internal.description}
                      <br />
                      <span className="text-xs italic">{pricingConfig.components.internal.additionalNotes}</span>
                    </p>
                  )}
                  
                  {/* Content Upload */}
                  {isContentUpload && (
                    <p className="text-muted-foreground">
                      <span className="font-medium">Content Upload ({getContentUploadLevelLabel()}) - {contentUploadHours} hours</span>
                      <br />
                      {pricingConfig.contentUpload[contentUploadLevel].internal.description}
                      <br />
                      <span className="text-xs italic">{pricingConfig.contentUpload[contentUploadLevel].internal.additionalNotes}</span>
                    </p>
                  )}
                  
                  {/* Migration */}
                  {isMigration && postTypeCount > 0 && (
                    <p className="text-muted-foreground">
                      <span className="font-medium">Content Migration ({postTypeCount} types) - {migrationHours} hours</span>
                      <br />
                      {pricingConfig.migration.internal.description}
                      <br />
                      <span className="text-xs italic">{pricingConfig.migration.internal.additionalNotes}</span>
                    </p>
                  )}
                  
                  {/* Selected Features */}
                  {selectedFeatures.map(feature => {
                    // Safe feature lookup to avoid any potential type issues
                    const featureConfig = pricingConfig.features as any;
                    const featureInfo = featureConfig[feature.id];
                    const description = featureInfo?.internal?.description || feature.description;
                    const additionalNotes = featureInfo?.internal?.additionalNotes;
                    
                    return (
                      <p key={feature.id} className="text-muted-foreground">
                        <span className="font-medium">{feature.name} - {formatHours(feature.minHours, feature.maxHours).replace(' hrs', ' hours')}</span>
                        <br />
                        {description}
                        {additionalNotes && (
                          <>
                            <br />
                            <span className="text-xs italic">{additionalNotes}</span>
                          </>
                        )}
                      </p>
                    );
                  })}
                  
                  {/* Internal Notes */}
                  {pricingConfig.generalNotes.internal.length > 0 && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="font-medium mb-2">Internal Notes:</p>
                      <ul className="list-disc pl-4 space-y-1">
                        {pricingConfig.generalNotes.internal.map((note, index) => (
                          <li key={index} className="text-muted-foreground">{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Total */}
                  <p className="text-muted-foreground font-bold pt-2 border-t border-border/50">
                    <span className="font-bold">Total Estimate - {formatHours(totalMinHours, totalMaxHours).replace(' hrs', ' hours')}</span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default HourCalculation; 